<script>
/* global axios */
// Admin provisioning of per-agent SIP extensions for a Voipe voice inbox (B2 / UX-1..3).
// Admin enters the extension + secret AS THEY EXIST in the client's Asterisk (Chatwoot does not
// generate them). Two independent checks per agent, deliberately:
//   • "Test" registers from the BROWSER — proves this device can connect over WSS.
//   • "Check configuration" asks the PBX — proves the extension is set up so calls actually work.
// The second is what stops an admin discovering a misconfigured extension only when a call never
// arrives, and it is the reason the read-only AMI connection exists.

export default {
  name: 'VoipeAgentEndpoints',
  props: {
    inboxId: { type: [Number, String], required: true },
    wssUrl: { type: String, default: '' },
    pbxHost: { type: String, default: '' },
  },
  data() {
    return {
      loading: true,
      loadError: '',
      webhookUrl: '',
      rows: [],
    };
  },
  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.loading = true;
      this.loadError = '';
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/smarticks/voice/endpoints`,
          { params: { inbox_id: this.inboxId } }
        );
        this.webhookUrl = data.event_webhook_url || '';
        this.rows = (data.agents || []).map(a => ({
          ...a,
          secret: '',
          test: null, // null | testing | ok | bad | unreachable
          saveState: null, // null | saved | failed
          findings: null, // null | 'checking' | array
        }));
      } catch (error) {
        // A failed load used to render an empty list with no message, which reads as
        // "this account has no agents" — a wrong answer presented as a fact.
        this.rows = [];
        this.loadError = this.$t('VOICE_VOIPE.ENDPOINTS.LOAD_FAILED');
      } finally {
        this.loading = false;
      }
    },
    async save(row) {
      if (!row.extension) return;
      row.saveState = null;
      try {
        await axios.post(
          `/api/v1/accounts/${this.accountId}/smarticks/voice/endpoints`,
          {
            inbox_id: this.inboxId,
            agent_id: row.agent_id,
            extension: row.extension,
            secret: row.secret,
          }
        );
        row.configured = true;
        row.secret = '';
        // Saving used to succeed in complete silence, so an admin could not tell it had worked.
        row.saveState = 'saved';
      } catch (error) {
        // And a failed save used to surface as row.test = 'bad', i.e. "wrong password" — blaming
        // the credentials for what was actually a save error.
        row.saveState = 'failed';
      }
    },
    // Asks the PBX, not the browser.
    async checkConfig(row) {
      if (!row.extension) return;
      row.findings = 'checking';
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/smarticks/voice/endpoints/check`,
          { params: { inbox_id: this.inboxId, extension: row.extension } }
        );
        row.findings = data.findings || [];
      } catch (error) {
        row.findings = [
          {
            key: 'connection',
            status: 'unknown',
            message: this.$t('VOICE_VOIPE.ENDPOINTS.UNKNOWN_HINT'),
          },
        ];
      }
    },
    async testConnection(row) {
      if (!row.extension || !row.secret || !this.wssUrl) return;
      row.test = 'testing';
      const { default: VoipeVoiceClient } = await import(
        'dashboard/api/channel/voice/voipeVoiceClient'
      );
      const client = new VoipeVoiceClient();
      let done = false;
      const finish = state => {
        if (done) return;
        done = true;
        row.test = state;
        client.disconnect();
      };
      client
        .on('registered', () => finish('ok'))
        .on('registrationFailed', () => finish('bad'))
        .on('disconnected', () => finish('unreachable'));
      try {
        await client.connect({
          wssUrl: this.wssUrl,
          pbxHost: this.pbxHost,
          extension: row.extension,
          secret: row.secret,
        });
      } catch (error) {
        finish('unreachable');
      }
      setTimeout(() => finish('unreachable'), 8000);
    },
    testLabel(row) {
      return {
        ok: this.$t('VOICE_VOIPE.ENDPOINTS.TEST_OK'),
        bad: this.$t('VOICE_VOIPE.ENDPOINTS.TEST_BAD'),
        unreachable: this.$t('VOICE_VOIPE.ENDPOINTS.TEST_UNREACHABLE'),
      }[row.test];
    },
    // "Unreachable" is NOT "wrong credentials", and painting both red sent the admin to fix a
    // password when the real problem was the network.
    testClass(row) {
      if (row.test === 'ok') return 'text-n-teal-11';
      if (row.test === 'unreachable') return 'text-n-amber-11';
      return 'text-n-ruby-11';
    },
    testIcon(row) {
      return {
        ok: 'i-ph-check-circle',
        bad: 'i-ph-x-circle',
        // The old ⛌ glyph is absent from most fonts and rendered as a tofu box.
        unreachable: 'i-ph-wifi-slash',
      }[row.test];
    },
    // Four states, and "unknown" is slate — never red, never green. Colouring an unchecked item red
    // trains admins to ignore red; colouring it green is the exact mistake §4F exists to prevent.
    findingClass(status) {
      return {
        ok: 'text-n-teal-11',
        warning: 'text-n-amber-11',
        blocked: 'text-n-ruby-11',
        unknown: 'text-n-slate-11',
        info: 'text-n-slate-11',
      }[status];
    },
    findingIcon(status) {
      return {
        ok: 'i-ph-check-circle',
        warning: 'i-ph-warning-circle',
        blocked: 'i-ph-x-circle',
        unknown: 'i-ph-question',
        info: 'i-ph-info',
      }[status];
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <p class="font-medium text-n-slate-12">
        {{ $t('VOICE_VOIPE.ENDPOINTS.TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ $t('VOICE_VOIPE.ENDPOINTS.HELP') }}
      </p>
    </div>

    <div v-if="webhookUrl">
      <p class="text-sm font-medium text-n-slate-12">
        {{ $t('VOICE_VOIPE.ENDPOINTS.WEBHOOK_TITLE') }}
      </p>
      <p class="text-xs text-n-slate-11 mb-1">
        {{ $t('VOICE_VOIPE.ENDPOINTS.WEBHOOK_HELP') }}
      </p>
      <woot-code :script="webhookUrl" />
    </div>

    <div v-if="loading" class="text-sm text-n-slate-11">
      {{ $t('VOICE_VOIPE.ENDPOINTS.LOADING') }}
    </div>

    <div v-else-if="loadError" class="text-sm text-n-ruby-11">
      {{ loadError }}
    </div>

    <template v-else>
      <!-- flex-col: the controls stay one row and the verdicts stack UNDERNEATH inside the same
           frame. One row cannot hold four verdicts, and at 375px it never could. -->
      <div
        v-for="row in rows"
        :key="row.agent_id"
        class="flex flex-col gap-2 border border-n-weak rounded-xl px-3 py-2.5 bg-white dark:bg-n-solid-2"
      >
        <div class="flex flex-wrap items-center gap-3">
          <div class="min-w-[120px]">
            <p class="text-sm font-semibold text-n-slate-12">{{ row.name }}</p>
            <p dir="ltr" class="text-xs text-n-slate-11">{{ row.email }}</p>
          </div>
          <input
            v-model="row.extension"
            dir="ltr"
            :placeholder="$t('VOICE_VOIPE.ENDPOINTS.EXTENSION')"
            class="w-24 border border-n-weak rounded-lg px-2.5 py-1.5 text-sm font-mono text-n-slate-12 bg-white dark:bg-n-solid-1"
          />
          <input
            v-model="row.secret"
            type="password"
            :placeholder="
              row.configured
                ? '•••••• ' + $t('VOICE_VOIPE.ENDPOINTS.CONFIGURED')
                : $t('VOICE_VOIPE.ENDPOINTS.SECRET')
            "
            class="w-28 border border-n-weak rounded-lg px-2.5 py-1.5 text-sm text-n-slate-12 bg-white dark:bg-n-solid-1"
          />
          <button
            class="text-sm font-medium border border-n-weak rounded-lg px-3 py-1.5 text-n-slate-12"
            @click="save(row)"
          >
            {{ $t('VOICE_VOIPE.ENDPOINTS.SAVE') }}
          </button>
          <button
            class="text-sm font-medium border border-n-weak rounded-lg px-3 py-1.5 text-n-slate-12 inline-flex items-center gap-1.5"
            :disabled="row.test === 'testing'"
            @click="testConnection(row)"
          >
            <i class="i-ph-plugs-connected" />
            {{ $t('VOICE_VOIPE.ENDPOINTS.TEST') }}
          </button>
          <button
            class="text-sm font-medium border border-n-weak rounded-lg px-3 py-1.5 text-n-slate-12 inline-flex items-center gap-1.5"
            :disabled="row.findings === 'checking'"
            @click="checkConfig(row)"
          >
            <i class="i-ph-stethoscope" />
            {{
              row.findings === 'checking'
                ? $t('VOICE_VOIPE.ENDPOINTS.CHECKING')
                : $t('VOICE_VOIPE.ENDPOINTS.CHECK')
            }}
          </button>
          <span
            v-if="row.saveState === 'saved'"
            class="text-sm font-medium text-n-teal-11"
          >
            {{ $t('VOICE_VOIPE.ENDPOINTS.SAVED') }}
          </span>
          <span
            v-else-if="row.saveState === 'failed'"
            class="text-sm font-medium text-n-ruby-11"
          >
            {{ $t('VOICE_VOIPE.ENDPOINTS.SAVE_FAILED') }}
          </span>
        </div>

        <div
          v-if="row.test && row.test !== 'testing'"
          class="text-sm font-medium inline-flex items-center gap-1.5"
          :class="testClass(row)"
        >
          <i :class="testIcon(row)" />
          {{ testLabel(row) }}
        </div>

        <ul
          v-if="Array.isArray(row.findings) && row.findings.length"
          class="flex flex-col gap-1"
        >
          <li
            v-for="f in row.findings"
            :key="f.key"
            class="text-sm inline-flex items-start gap-1.5"
            :class="findingClass(f.status)"
          >
            <i :class="findingIcon(f.status)" class="mt-0.5 shrink-0" />
            <span>{{ f.message }}</span>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>
